import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Screen from '../../components/common/Screen';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import NotFound from '../../components/common/NotFound';
import { formatDate, handleBranding } from '../../utils';
import '../readiness/PublicReadiness.css';

/* The QR code at the venue: tornea.app/<storefront-slug>/support[/<eventId>].
   Three fields and one button — the person standing at the gate becomes a supporter of the
   event (a team-less player, so they show on Machina's Fans ▸ Supporters) and lands on its Home.
   An address we already know is registered too, but signed in by the link we mail; a typed
   address never opens somebody's account. Backed by routes/supporter_access.py. */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SupporterAccess = () => {
  const { t } = useTranslation();
  const { slug, eventId } = useParams();

  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState('loading');   // loading | ok | notfound
  const [outcome, setOutcome] = useState(null);      // check_mail | login_required | mail_failed
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', website: '' });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!slug) { setStatus('notfound'); return undefined; }
    let cancelled = false;
    // Somebody scanning a poster has never seen the storefront: pin the brand first, so the
    // login screen they may meet later follows the brand too.
    localStorage.setItem('brandSlug', slug);
    handleBranding();
    const path = eventId ? `/api/supporter-access/${slug}/${eventId}` : `/api/supporter-access/${slug}`;
    fetch(path, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((res) => { if (!cancelled) { setConfig(res); setStatus('ok'); } })
      .catch(() => { if (!cancelled) setStatus('notfound'); });
    return () => { cancelled = true; };
  }, [slug, eventId]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = t('freeAccountErrorRequired');
    if (!form.last_name.trim()) e.last_name = t('freeAccountErrorRequired');
    if (!form.email.trim()) e.email = t('freeAccountErrorRequired');
    else if (!EMAIL_RE.test(form.email.trim())) e.email = t('freeAccountErrorEmail');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (asSession = false) => {
    if (!asSession && !validate()) return;
    setSubmitting(true);
    setFormError(null);
    try {
      // Raw fetch, like the other public pages: apiRequest treats a 401 as a dead session.
      const resp = await fetch(`/api/supporter-access/${slug}/${config.event.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(asSession ? {} : {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          website: form.website,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.redirect) {
        // The JWT arrives as an HttpOnly cookie; this flag is what the SPA reads. A stale
        // event link from an earlier visit must not re-scope the next login.
        localStorage.setItem('isLoggedIn', '1');
        sessionStorage.removeItem('otherEventId');
        // A full load rather than navigate(): the app picks up the event's branding on boot.
        window.location.assign(data.redirect);
        return;
      }
      if (resp.ok && data.status) { setOutcome(data.status); window.scrollTo(0, 0); return; }
      setFormError(t(data.message === 'invalidEmail' ? 'freeAccountErrorEmail'
        : data.message === 'supporterAccessClosed' ? 'supporterAccessClosed'
          : 'supporterAccessError'));
    } catch (err) {
      setFormError(t('supporterAccessError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return <Screen><div className="top"><Card><span className="auth__spinner" /></Card></div></Screen>;
  }
  if (status === 'notfound') return <NotFound message={t('storefrontNotFound')} />;

  const { brand, event, session } = config;
  const accentStyle = brand.primary_color ? { '--bravos-accent': brand.primary_color } : undefined;
  const loginLink = event ? `/login?otherEventId=${event.id}` : '/login';

  const field = (key, labelKey, type, autoComplete) => (
    <div className="mb-3 text-start">
      <label className="form-label fw-bold" htmlFor={`sa_${key}`}>
        {t(labelKey)}<span className="text-danger"> *</span>
      </label>
      <input
        id={`sa_${key}`}
        type={type}
        autoComplete={autoComplete}
        className={`form-control${errors[key] ? ' is-invalid' : ''}`}
        value={form[key]}
        onChange={(e) => setField(key, e.target.value)}
      />
      {errors[key] && <div className="invalid-feedback d-block">{errors[key]}</div>}
    </div>
  );

  let body;
  if (!event) {
    body = <p className="text-muted text-center py-3">{t('supporterAccessNoEvent')}</p>;
  } else if (!config.open) {
    body = <p className="text-muted text-center py-3">{t('supporterAccessClosed')}</p>;
  } else if (outcome) {
    body = (
      <div className="text-center py-3">
        <Title>{t(outcome === 'check_mail' ? 'supporterAccessSentTitle' : 'supporterAccessRegisteredTitle')}</Title>
        <p className="text-muted mx-2">
          {outcome === 'check_mail' && t('supporterAccessSentBody', { email: form.email.trim() })}
          {outcome === 'login_required' && t('supporterAccessLoginBody')}
          {outcome === 'mail_failed' && t('supporterAccessMailFailed')}
        </p>
        {outcome !== 'check_mail' && (
          <a className="bravos-submit" href={loginLink}><Icon name="sign-in-alt" /> {t('login')}</a>
        )}
      </div>
    );
  } else {
    body = (
      <>
        <Title>{t('supporterAccessTitle')}</Title>
        <p className="text-muted text-start mx-2">{t('supporterAccessIntro', { event: event.name })}</p>

        {session ? (
          <button type="button" className="bravos-submit" onClick={() => submit(true)} disabled={submitting}>
            {submitting ? <span className="auth__spinner" /> : <Icon name="megaphone" />}
            {t('supporterAccessContinueAs', { name: session.first_name || session.email })}
          </button>
        ) : (
          <>
            {field('first_name', 'freeAccountFirstName', 'text', 'given-name')}
            {field('last_name', 'freeAccountLastName', 'text', 'family-name')}
            {field('email', 'freeAccountEmail', 'email', 'email')}

            {/* Honeypot — hidden from humans. */}
            <div aria-hidden="true"
                 style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
              <label htmlFor="sa_website">{t('formLeaveBlank')}</label>
              <input type="text" id="sa_website" tabIndex={-1} autoComplete="off"
                     value={form.website} onChange={(e) => setField('website', e.target.value)} />
            </div>

            {formError && <div className="alert alert-danger" role="alert">{formError}</div>}

            <button type="button" className="bravos-submit" onClick={() => submit(false)} disabled={submitting}>
              {submitting ? <span className="auth__spinner" /> : <Icon name="megaphone" />}
              {t('supporterAccessSubmit')}
            </button>
          </>
        )}
        {session && formError && <div className="alert alert-danger mt-3" role="alert">{formError}</div>}
      </>
    );
  }

  return (
    <Screen><div className="top">
      <Card>
        <div className="readiness" style={accentStyle}>
          {brand.logo_url && <img src={brand.logo_url} alt={brand.name} className="readiness__logo" />}
          {event && (
            <p className="text-muted small text-center mb-2">
              {event.name}
              {event.date && ` · ${formatDate(event.date, event.time_zone)}`}
              {event.location_name && ` · ${event.location_name}`}
            </p>
          )}
          {body}
        </div>
      </Card>
    </div></Screen>
  );
};

export default SupporterAccess;
